import { Button, Grid, styled } from "@mui/material";
import AppModal from "components/AppModal";
import FlexBox from "components/flexbox/FlexBox";
import AppTextField from "components/input-fields/AppTextField";
import Scrollbar from "components/ScrollBar";
import { H2, H6 } from "components/Typography";
import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import apiService from "Network/apiService";
import * as Yup from "yup";

const StyledAppModal = styled(AppModal)(({ theme }) => ({
  maxWidth: 450,
  minWidth: 200,
  [theme.breakpoints.down(325)]: {
    maxWidth: "100%"
  }
}));

const AddRegionModal = ({ 
  open, 
  onClose, 
  edit, 
  data, 
  districtId,
  onSuccess 
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (edit && data) {
      setName(data.region_name || "");
      setDescription(data.region_description || "");
    } else {
      setName("");
      setDescription("");
    }
  }, [edit, data]);

  const fieldValidationSchema = Yup.object().shape({
    name: Yup.string().min(3, "Too Short").required("Name is Required!"),
    description: Yup.string(),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await fieldValidationSchema.validate({ name, description }, { abortEarly: false });

      const payload = {
        name,
        description,
      };

      if (edit) {
        payload.region_id = data.id;
      } else {
        payload.district_id = districtId;
      }

      const response = await apiService[edit ? 'put' : 'post'](
        edit ? API_ENDPOINTS.PUT_update_region : API_ENDPOINTS.POST_create_region,
        payload
      );

      if (response?.success) {
        toast.success(`Region ${edit ? 'updated' : 'created'} successfully!`);
        await onSuccess?.(); // Wait for refresh to complete
        onClose();
        setName("");
        setDescription("");
      } else {
        toast.error(response?.message || `Failed to ${edit ? 'update' : 'create'} region.`);
      }
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const validationErrors = {};
        error.inner.forEach(err => {
          validationErrors[err.path] = err.message;
        });
        toast.error("Please fill all required fields correctly.");
      } else {
        console.error("API error:", error);
        toast.error("An unexpected error occurred.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StyledAppModal open={open} handleClose={onClose}>
      <H2 mb={2}>{edit ? "Edit Region" : "Add new Region"}</H2>

      <form onSubmit={handleSubmit}>
        <Scrollbar style={{ maxHeight: 400 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <H6 mb={1}>Region Name</H6>
              <AppTextField
                fullWidth
                size="small"
                name="name"
                placeholder="Region Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isLoading}
              />
            </Grid>

            <Grid item xs={12}>
              <H6 mb={1}>Description</H6>
              <AppTextField
                fullWidth
                size="small"
                multiline
                rows={4}
                name="description"
                placeholder="Region Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isLoading}
              />
            </Grid>
          </Grid>
        </Scrollbar>

        <FlexBox justifyContent="flex-end" gap={2} marginTop={4}>
          <Button 
            fullWidth 
            size="small" 
            variant="outlined" 
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button 
            fullWidth 
            size="small" 
            type="submit" 
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : (edit ? "Update" : "Save")}
          </Button>
        </FlexBox>
      </form>
    </StyledAppModal>
  );
};

export default AddRegionModal;
